// Routine / Daily API client for the web app. Talks to the same Django
// endpoints the mobile app uses (backend/api/views.py: create_routine,
// get_routines, update_routine, delete_routine, instantiate_routine) so a
// routine created on web shows up correctly on mobile and vice-versa.
//
// Field shapes mirror the mobile payload exactly — see the contract in
// views.py lines 1677–1915. Notably: days_of_week is a CSV string
// ("Mon,Wed,Fri", empty = every day); `time` is a CSV of 12-hour strings
// ("09:00 AM" or "09:00 AM,06:00 PM"); interval mode uses interval_minutes
// (int|null) + window_start/window_end ("8:00 AM" style); the active toggle
// uses `is_active` on update.

import axios from 'axios';
import API_BASE_URL from '../config';

const BASE_URL = `${API_BASE_URL}/api`;

const getAuthHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    window.location.href = '/login';
    throw new Error('No access token found');
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
};

const handle401 = (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  }
};

// GET /api/routine/ → Routine[]
export const fetchRoutines = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/routine/`, { headers: getAuthHeaders() });
    return Array.isArray(res.data.routines) ? res.data.routines : [];
  } catch (err) {
    console.error('❌ Error fetching routines:', err.response?.data || err.message);
    handle401(err);
    throw err;
  }
};

// POST /api/routine/create/ → { routine }
export const createRoutine = async (payload) => {
  try {
    const res = await axios.post(`${BASE_URL}/routine/create/`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data.routine;
  } catch (err) {
    console.error('❌ Error creating routine:', err.response?.data || err.message);
    handle401(err);
    throw err;
  }
};

// PUT /api/routine/<id>/ → routine (flat)
export const updateRoutine = async (id, patch) => {
  try {
    const res = await axios.put(`${BASE_URL}/routine/${id}/`, patch, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error('❌ Error updating routine:', err.response?.data || err.message);
    handle401(err);
    throw err;
  }
};

// DELETE /api/routine/<id>/delete/
export const deleteRoutine = async (id) => {
  try {
    const res = await axios.delete(`${BASE_URL}/routine/${id}/delete/`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error('❌ Error deleting routine:', err.response?.data || err.message);
    handle401(err);
    throw err;
  }
};

// POST /api/routine/<id>/instantiate/ → { task } — drops a task for the given date.
export const instantiateRoutine = async (id, { year, month, week, day }) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/routine/${id}/instantiate/`,
      { year, month, week, day },
      { headers: getAuthHeaders() },
    );
    return res.data.task;
  } catch (err) {
    console.error('❌ Error instantiating routine:', err.response?.data || err.message);
    handle401(err);
    throw err;
  }
};
